/**
 * `ViewBarPane` — the surface every control on the non-table board toolbar opens (round 8e).
 *
 * **The defect it removes.** 8b (P-025b) gave `BoardViewToolbar` its own little `Dropdown`:
 * `.ez-view-bar__pane { position: absolute; inset-block-start: 36px; inset-inline-start: 38px }`
 * inside `.ez-view-bar`, next to a `position: fixed` scrim. That is a parallel implementation of
 * something the product already had, and on a phone it is broken twice over, because `mobile.css`
 * F9-D5 makes `.ez-toolbar` a **horizontal scroller**:
 *
 *   - an absolutely positioned child of a scrolled container moves with the scroll — the 8e sweep
 *     measured `left −386 … −442` on a 390 px viewport (`overlay-offscreen`, 54);
 *   - and it is *clipped* by that scroller, so `elementFromPoint` at the pane's own centre returns
 *     the only thing still painted there — the pane's own scrim, which is `position: fixed` and
 *     escapes the clip (`overlay-covered`, 78, every one of them naming `ez-view-bar__scrim`).
 *
 * No z-index fixes either: the scrim is already on `--ez-layer-scrim` (150), under
 * `--ez-layer-popover` (500). A pane inside a scroller cannot be placed by CSS at all.
 *
 * **What it does instead** — the two primitives the rest of the product already uses, and nothing
 * new:
 *
 *   1. it renders through a **body portal**, like `AnchoredPopover` (P-005), so no ancestor's
 *      scroll, clip or stacking context can reach it, and it places itself with the *shared*
 *      `placeAnchored` / `isUsableAnchor` from `@elizmonday/ui` (P-007/P-008, round 7a) — clamped
 *      into the viewport on both axes, flipping above the bar when there is no room below, and
 *      refusing a degenerate anchor rather than painting in the corner of the window;
 *   2. it carries the table's own `.ez-board-pane` class, so a phone converts it into the **same
 *      F9-D6b bottom sheet** as the table toolbar's Person / Filter / Sort / Group by panes —
 *      full width, pinned to the bottom, its own scroll, the same grab handle — from the rules
 *      that are already in `mobile.css`, and a `role="dialog"` like `panes/Pane.tsx`, which is
 *      what tells the sweep this is a docked surface rather than an anchored menu.
 *
 * **Desktop geometry is unchanged, by construction.** `.ez-toolbar` has `padding: 0 38px`, so 8b's
 * `inset-inline-start: 38px` is the left edge of the bar's *first control* and its
 * `inset-block-start: 36px` is 4 px under the 32 px band. The anchor is therefore that first
 * control and the gap is `OVERLAY_GAP` (4): `placeAnchored` returns exactly the same point on a
 * desktop viewport, where nothing clamps. Only the containing block changed.
 */
import { OVERLAY_GAP, isUsableAnchor, placeAnchored, type OverlayRect } from '@elizmonday/ui';
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** 8b's measured widths: the quick-filter strip is 800, every other pane 280. */
const PANE_WIDTH = 280;
const WIDE_PANE_WIDTH = 800;

export interface ViewBarPaneProps {
  /**
   * The bar's **leading control**. Every pane on this bar opens at the left edge of the bar's
   * content, which is where 8b measured it — not under the control that was clicked.
   */
  anchor: HTMLElement | null;
  label: string;
  testId: string;
  wide?: boolean;
  onClose(): void;
  children: ReactNode;
}

export function ViewBarPane({ anchor, label, testId, wide = false, onClose, children }: ViewBarPaneProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);
  /** P-007: the last rectangle the anchor was known to have; a degenerate one is not an anchor. */
  const lastAnchorRect = useRef<OverlayRect | null>(null);

  const place = useCallback(() => {
    const live = anchor?.getBoundingClientRect();
    const box = isUsableAnchor(live) ? live : lastAnchorRect.current;
    if (!box) return;
    lastAnchorRect.current = { x: box.x, y: box.y, width: box.width, height: box.height };

    const own = ref.current?.getBoundingClientRect();
    const next = placeAnchored(
      box,
      { width: own?.width ?? (wide ? WIDE_PANE_WIDTH : PANE_WIDTH), height: own?.height ?? 0 },
      { width: window.innerWidth, height: window.innerHeight },
      { align: 'start', gap: OVERLAY_GAP },
    );
    setPosition((current) =>
      current && current.top === next.top && current.left === next.left ? current : { top: next.top, left: next.left },
    );
    setMaxWidth((current) => (current === next.maxWidth ? current : next.maxWidth));
  }, [anchor, wide]);

  useLayoutEffect(() => {
    place();
    // `true`: the bar is a horizontal scroller on a phone, and that scroll does not bubble.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [place, children]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {/*
        The scrim stays what it always was — a transparent full-viewport button that closes the
        pane on a click outside — but it is now a *sibling* of the pane in `<body>` instead of a
        child of the toolbar, so it can never be the thing painted where the pane should be. It is
        a `<button>`, which `tools/qa` classes as a control and never as an overlay.
      */}
      {/* P-145: the ledger counts by `data-testid`; the dismiss scrim had none. */}
      <button
        type="button"
        className="ez-view-bar__scrim"
        data-testid={`${testId}-scrim`}
        aria-label={`Close ${label}`}
        onClick={onClose}
      />
      <div
        ref={ref}
        className="ez-board-pane ez-view-bar__pane"
        role="dialog"
        aria-label={label}
        data-testid={testId}
        data-wide={wide || undefined}
        /*
         * Hidden rather than painted at (0, 0) until it has been measured: one frame in the
         * corner of the window is the defect P-007 reports, and a person sees that frame.
         */
        style={{
          top: position?.top ?? 0,
          left: position?.left ?? 0,
          visibility: position === null ? 'hidden' : undefined,
          ...(maxWidth === null ? undefined : { maxWidth }),
        }}
      >
        <header className="ez-view-bar__pane-title">{label}</header>
        {children}
      </div>
    </>,
    document.body,
  );
}
